import { useEffect, useState } from 'react';

interface CountdownTime {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    isExpired: boolean; // Whether countdown has expired
}

/**
 * Countdown Hook
 * @param targetTimestamp Target timestamp in milliseconds
 * @param updateInterval Update interval in milliseconds (default: 1000ms)
 * @returns Countdown data { days, hours, minutes, seconds, isExpired }
 */
export function useCountdown(targetTimestamp: number | null, updateInterval = 1000): CountdownTime {
    const [countdown, setCountdown] = useState<CountdownTime>(() => calculateTimeRemaining(targetTimestamp));

    useEffect(() => {
        // Don't start timer if already expired
        if (countdown.isExpired) return;

        const timer = setInterval(() => {
            const newTime = calculateTimeRemaining(targetTimestamp);
            setCountdown(newTime);

            // Clear timer when countdown completes
            if (newTime.isExpired) {
                clearInterval(timer);
            }
        }, updateInterval);

        return () => clearInterval(timer);
    }, [targetTimestamp, updateInterval, countdown.isExpired]);

    return countdown;
}

/** Calculate remaining time */
function calculateTimeRemaining(targetTimestamp: number | null): CountdownTime {
    const now = Date.now();
    const diff = targetTimestamp ? Math.max(targetTimestamp - now, 0) : 0; // ensure non-negative

    // Calculate time units
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return {
        days,
        hours,
        minutes,
        seconds,
        isExpired: diff === 0, // Whether countdown has reached zero
    };
}
